import { useState, useEffect } from "react";
import AudioPlayer from "../dictadle/components/audio-player/AudioPlayer"; 
import InputBox from "../dictadle/components/input-box/InputBox"; 
import HighlightComponent from "../dictadle/components/word-hightlighter/SpanHighlighter"
import {CongratsModal} from "../dictadle/components/modals/CongratulateModal"
import { Alert } from "../dictadle/components/alerts/Alert";
import Header from "../components/common/Header";
import {Helmet} from 'react-helmet'


const Dictadle = () => {
  const [audioAnswer, setAudioAnswer] = useState<string>(""); 
  const [audioText, setAudioText] = useState<string>("")
//   const [guess, setGuess] = useState<string>(""); 
  const [attempts, setAttempts] = useState<number>(0); 
  const [pastGuesses, setPastGuesses] = useState<any[]>([]); // Track past guesses with highlighted results
  const [isGameWon, setisGameWon] = useState<boolean>(false); // State for tracking if the user has won
  const [isWinModalOpen, setIsWinModalOpen] = useState(false);

  useEffect(() => {
    if (isGameWon) {
      setIsWinModalOpen(true);
    }
  }, [isGameWon]);

  const normalizeString = (str: string) => {
    return str
      .toLowerCase()                    // Convert to lowercase
      .replace(/[^\w\s]/g, "")           // Remove punctuation
      .trim()                            // Remove extra spaces
      .split(/\s+/)                      // Split by spaces
      .join(" ");                        // Join back into a single string with spaces
  };

  const handleGuess = (userGuess: string) => {
    const normalizedGuess = normalizeString(userGuess);
    const normalizedAnswer = normalizeString(audioText);
  
    if (normalizedGuess === normalizedAnswer) {
      setisGameWon(true); // Set isGameWon to true if the normalized guess matches
    }
    const matchedResult = processGuess(userGuess); 
    setPastGuesses((prevGuesses) => [...prevGuesses, matchedResult]); 
    setAttempts(attempts + 1);
    // setGuess(userGuess);
  };

  const processGuess = (userGuess: string) => {
    return {
      guess: userGuess,
      highlight: <HighlightComponent target={audioText} guess={userGuess} /> // Highlighted guess
    };
  };

  useEffect(() => {
    async function fetchAudio() {
      try {
        setAudioAnswer("https://lf9-survey.bytetos.com/obj/survey-public/img-6270c574-a348-4578-9096-8ea444e991a9"); 
        setAudioText("Having a lot of different kinds of things at moderate numbers is better than having a lot of any one thing.");
      } catch (error) {
        console.error("Error fetching audio:", error);
      }
    }
    fetchAudio();
  }, []);


  return (
    <div>
      <Helmet>
        {/* Page Title */}
        <title>Dictadle - Listen and Guess the Sentence in 5 Attempts</title>
        
        <meta 
          name="description" 
          content="Play Dictadle, a fun and challenging game where you listen to an audio clip and try to guess the sentence. You have 5 attempts to guess correctly, with color feedback for each guess!" 
        />
        <meta 
          name="keywords" 
          content="Dictadle, listen and guess game, audio dictation game, guess the sentence, word guessing game, language learning game, sentence puzzle" 
        />
        <meta name="author" content="GamesLikeWordle" />

        {/* Open Graph for Social Media */}
        <meta property="og:title" content="Dictadle - Listen and Guess the Sentence in 5 Attempts" />
        <meta 
          property="og:description" 
          content="Play Dictadle, the ultimate listening and guessing game. Listen to audio clips, guess the sentence, and get feedback to improve your language skills!" 
        />
        <meta property="og:url" content="https://www.gameslikewordle.com/dictadle" />
        <meta property="og:type" content="website" />
        {/* <meta property="og:image" content="https://www.gameslikewordle.com/assets/dictadle-og.jpg" /> */}

        {/* Twitter SEO */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Dictadle - Listen and Guess the Sentence in 5 Attempts" />
        <meta 
          name="twitter:description" 
          content="Dive into Dictadle, the listening and guessing game. Try to guess the sentence in 5 attempts with real-time feedback to enhance your language skills!" 
        />
        <meta name="twitter:image" content="https://www.gameslikewordle.com/assets/dictadle-twitter.jpg" />
        <meta name="twitter:site" content="@GamesLikeWordle" />

        {/* Canonical URL */}
        <link rel="canonical" href="https://www.gameslikewordle.com/dictadle" />
      </Helmet>
      <Header></Header>

      <div className="my-6 p-6 rounded-lg shadow-md text-gray-800 w-full max-w-3xl mx-auto">
        <p className="text-base sm:text-lg md:text-xl text-gray-700 mb-4">
          Welcome to <span className="font-semibold text-indigo-600">Dictadle</span>! This exciting game combines the classic fun of Wordle with the educational power of daily dictation. In this game, you’ll listen to an audio clip and try to guess the sentence. You have 5 attempts to get it right. Each guess provides color feedback to help you along the way:
        </p>
        <ul className="list-inside pl-4 space-y-2 text-sm sm:text-base md:text-lg text-gray-600">
          <li className="flex items-center">
            <span className="block w-2.5 h-2.5 mr-2 rounded-full bg-green-500"></span>
            Words that are in the sentence will turn&nbsp;<strong className="text-green-500">green</strong>.
          </li>
          <li className="flex items-center">
            <span className="block w-2.5 h-2.5 mr-2 rounded-full bg-yellow-500"></span>
            Words that are nearly correct will be&nbsp;<strong className="text-yellow-500">yellow</strong>.
          </li>
          <li className="flex items-center">
            <span className="block w-2.5 h-2.5 mr-2 rounded-full bg-gray-500"></span>
            Words that do not appear at all will be&nbsp;<strong className="text-gray-500">gray</strong>.
          </li>
        </ul>
      </div>

      {/* <div className="my-6 bg-blue-50 p-6 rounded-lg shadow-md text-gray-800 w-3/4 mx-auto"> */}
      <div className="my-6 p-6 rounded-lg shadow-md text-gray-800 w-full max-w-3xl mx-auto">
        <div className="my-8">
          <AudioPlayer audioAnswer={audioAnswer} textAnswer={audioText} />
        </div>
      
        <div className="my-6 space-y-2">
            {pastGuesses.map((result, index) => (
              <div key={index} className="flex space-x-2">
                <span>{result.highlight}</span>
              </div>
            ))}
        </div>

        <div className="my-6">
          <InputBox onSubmit={handleGuess} invisible={attempts >= 5 || isGameWon} />
        </div>
        <div>
          {attempts < 5 && !isGameWon && (<p>You have {5 - attempts} attempts left</p>)}
          <Alert message={`💪 Keep trying! The correct answer is: ${audioText} Don't give up, you'll get it next time!`}
          isOpen={attempts>=5&&!isGameWon} />

          <CongratsModal
              isOpen={isWinModalOpen}
              handleClose={() => setIsWinModalOpen(false)}
              title="🎉 Congratulations! You guessed the correct answer!"
              content="Well done! Keep practicing to improve even more."
          />
        </div>
      </div>
    </div>
  );
};

export default Dictadle;